import { useNavigate, useParams } from 'react-router-dom';
import { FlaskConical, ArrowLeft, Lock, CheckCircle, Play, LogOut, BarChart3 } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { useProgress } from '@/context/ProgressContext';
import { COURSES, getExperimentList } from '@/config/courses.config';

export default function DashboardPage() {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const { state, logout, setLabMode } = useApp();
  const { getExperimentProgress, getCourseProgress } = useProgress();

  const course = courseId ? COURSES[courseId] : null;
  const experiments = courseId ? getExperimentList(courseId) : [];
  const overallProgress = courseId ? getCourseProgress(courseId) : 0;

  if (!course) {
    return (
      <div className="error-page">
        <h1>Course not found</h1>
        <button onClick={() => navigate('/courses')}>Back to Courses</button>
      </div>
    );
  }

  const handleExperimentClick = (expId: string, status: string) => {
    if (status === 'locked') return;
    navigate(`/course/${courseId}/experiment/${expId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="dashboard-page">
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/courses')}>
            <ArrowLeft size={20} />
          </button>
          <div className="brand">
            <FlaskConical size={24} />
            <span>BDP vLab</span>
          </div>
          <span className="course-badge">{course.code}</span>
        </div>
        <div className="header-right">
          <button
            className="my-progress-btn"
            onClick={() => navigate(`/course/${courseId}/progress`)}
          >
            <BarChart3 size={16} />
            My Progress
          </button>
          <div className="mode-toggle">
            <button
              className={`mode-btn ${state.labMode === 'guided' ? 'active' : ''}`}
              onClick={() => setLabMode('guided')}
            >
              Guided
            </button>
            <button
              className={`mode-btn ${state.labMode === 'free' ? 'active' : ''}`}
              onClick={() => setLabMode('free')}
            >
              Free
            </button>
          </div>
          <span className="user-name">
            {state.studentInfo?.name || 'Student'}
          </span>
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="page-content">
        <div className="course-header">
          <h1>{course.fullName}</h1>
          <p>{course.description}</p>
          <div className="progress-section">
            <span>Overall Progress: {overallProgress}%</span>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${overallProgress}%` }} />
            </div>
          </div>
        </div>

        <div className="experiments-grid">
          {experiments.map(exp => {
            const progress = getExperimentProgress(courseId!, exp.id);
            const isCompleted = progress && progress.partsCompleted.length >= 2;
            const isLocked = exp.status === 'locked';

            return (
              <div
                key={exp.id}
                className={`experiment-card ${isLocked ? 'locked' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={() => handleExperimentClick(exp.id, exp.status)}
              >
                <div className="exp-number">
                  {isLocked ? <Lock size={20} /> : isCompleted ? <CheckCircle size={20} /> : exp.number}
                </div>
                <div className="exp-content">
                  <h3>Experiment {exp.number}</h3>
                  <p>{exp.name}</p>
                </div>
                {!isLocked && (
                  <button className="start-btn">
                    <Play size={16} />
                    {progress ? 'Continue' : 'Start'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <style>{`
        .dashboard-page {
          min-height: 100vh;
          background: var(--bg-dark, #1A1A1A);
          color: var(--text-primary, #FFFFFF);
        }
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 24px;
          background: var(--bg-surface, #2C2C2C);
          border-bottom: 1px solid var(--border, #4A4A4A);
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .back-btn {
          background: transparent;
          border: none;
          color: var(--text-secondary, #CCCCCC);
          cursor: pointer;
          padding: 8px;
          display: flex;
          align-items: center;
        }
        .back-btn:hover {
          color: var(--primary, #FF5A36);
        }
        .brand {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 18px;
          font-weight: 700;
          color: var(--primary, #FF5A36);
        }
        .course-badge {
          background: var(--primary, #FF5A36);
          color: white;
          padding: 4px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }
        .header-right {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .mode-toggle {
          display: flex;
          background: var(--bg-dark, #1A1A1A);
          border-radius: 6px;
          padding: 2px;
        }
        .mode-btn {
          background: transparent;
          border: none;
          color: var(--text-muted, #999999);
          padding: 6px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          transition: all 0.2s ease;
        }
        .mode-btn.active {
          background: var(--primary, #FF5A36);
          color: white;
        }
        .user-name {
          color: var(--text-secondary, #CCCCCC);
          font-size: 14px;
        }
        .logout-btn {
          background: transparent;
          border: 1px solid var(--border, #4A4A4A);
          color: var(--text-secondary, #CCCCCC);
          padding: 8px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
        }
        .logout-btn:hover {
          border-color: var(--danger, #EF4444);
          color: var(--danger, #EF4444);
        }
        .page-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 32px 24px;
        }
        .course-header {
          margin-bottom: 32px;
        }
        .course-header h1 {
          font-size: 28px;
          margin: 0 0 8px 0;
        }
        .course-header p {
          color: var(--text-muted, #999999);
          margin: 0 0 16px 0;
        }
        .progress-section {
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 14px;
          color: var(--text-secondary, #CCCCCC);
        }
        .progress-bar {
          flex: 1;
          max-width: 300px;
          height: 8px;
          background: var(--bg-surface, #2C2C2C);
          border-radius: 4px;
          overflow: hidden;
        }
        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--secondary, #00A86B), var(--secondary-dark, #2E8B57));
          border-radius: 4px;
        }
        .experiments-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 16px;
        }
        .experiment-card {
          background: var(--bg-surface, #2C2C2C);
          border: 1px solid var(--border, #4A4A4A);
          border-radius: 10px;
          padding: 16px;
          display: flex;
          align-items: center;
          gap: 16px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .experiment-card:hover:not(.locked) {
          border-color: var(--primary, #FF5A36);
          transform: translateY(-2px);
        }
        .experiment-card.locked {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .experiment-card.completed {
          border-color: var(--secondary, #00A86B);
        }
        .exp-number {
          width: 48px;
          height: 48px;
          background: var(--bg-elevated, #3A3A3A);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: 700;
          color: var(--primary, #FF5A36);
        }
        .experiment-card.completed .exp-number {
          background: var(--secondary, #00A86B);
          color: white;
        }
        .experiment-card.locked .exp-number {
          color: var(--text-muted, #999999);
        }
        .exp-content {
          flex: 1;
        }
        .exp-content h3 {
          font-size: 14px;
          margin: 0 0 4px 0;
          color: var(--text-secondary, #CCCCCC);
        }
        .exp-content p {
          font-size: 16px;
          margin: 0;
          font-weight: 500;
        }
        .start-btn {
          background: var(--primary, #FF5A36);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
        .start-btn:hover {
          background: var(--primary-dark, #E04420);
        }
        .my-progress-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          background: transparent;
          border: 1px solid var(--secondary, #00A86B);
          color: var(--secondary, #00A86B);
          padding: 6px 14px;
          border-radius: 6px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .my-progress-btn:hover {
          background: var(--secondary, #00A86B);
          color: white;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 168, 107, 0.3);
        }
        .error-page {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          gap: 16px;
        }
      `}</style>
    </div>
  );
}
